import React, { useState, useEffect } from 'react';
import { formatDateOnly } from '../../utils/dates';
import { API_BASE } from '../../config';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { useConfirm } from '../ConfirmProvider';
function ViewReportResponsesModal({ reportId, onClose, fetchWithAuth }) {
  const confirm = useConfirm();
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_count: 0,
    unread_count: 0,
    urgent_count: 0
  });
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterViewed, setFilterViewed] = useState('all');

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const url = reportId
        ? `${API_BASE}/api/reports/${reportId}/responses`
        : `${API_BASE}/api/reports/responses`;
      const response = await fetchWithAuth(url);
      if (response.ok) {
        const data = await response.json();
        setResponses(data.responses);
        setStats({
          total_count: data.total_count,
          unread_count: data.unread_count,
          urgent_count: data.urgent_count
        });
      }
    } catch (error) {
      console.error('Failed to load responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (responseId) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/reports/responses/${responseId}/mark-read`,
        { method: 'PUT' }
      );

      if (response.ok) {
        // Update local state
        setResponses(prev => prev.map(r =>
          r.response_id === responseId
            ? { ...r, user_viewed: true, user_viewed_at: new Date() }
            : r
        ));

        // Update stats
        setStats(prev => ({
          ...prev,
          unread_count: Math.max(0, prev.unread_count - 1)
        }));
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    if (!response.user_viewed) {
      handleMarkAsRead(response.response_id);
    }
  };

  const handleDeleteResponse = async (responseId, event) => {
    event?.stopPropagation();

    if (!(await confirm({
      title: 'Delete this response?',
      message: 'The provider response will be permanently removed from this report. This cannot be undone.',
      confirmLabel: 'Delete Response',
      destructive: true,
    }))) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/reports/responses/${responseId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setResponses(prev => prev.filter(r => r.response_id !== responseId));
        setStats(prev => ({ ...prev, total_count: prev.total_count - 1 }));
      } else {
        alert('Failed to delete response.');
      }
    } catch (error) {
      console.error('Failed to delete response:', error);
      alert('An error occurred.');
    }
  };

  const handleDeleteReport = async (reportId, event) => {
    event.stopPropagation(); // Prevent opening the response detail

    if (!(await confirm({
      title: 'Delete this report?',
      message: 'The report and every response it has collected will be permanently deleted. This cannot be undone.',
      confirmLabel: 'Delete Report',
      destructive: true,
    }))) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/reports/${reportId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        // Remove from local state
        setResponses(prev => prev.filter(r => r.report_id !== reportId));

        // Update stats
        setStats(prev => ({
          total_count: prev.total_count - 1,
          unread_count: prev.unread_count,
          urgent_count: prev.urgent_count
        }));
      } else {
        alert('Failed to delete report. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('An error occurred while deleting the report.');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'general': 'General Comments',
      'medication_change': 'Medication Change',
      'diet_recommendation': 'Diet Recommendation',
      'exercise_recommendation': 'Exercise Recommendation',
      'follow_up_required': 'Follow-up Required',
      'urgent': 'Urgent'
    };
    return labels[category] || category;
  };

  // Filter responses
  const filteredResponses = responses.filter(r => {
    if (filterCategory !== 'all' && r.response_category !== filterCategory) {
      return false;
    }
    if (filterViewed === 'unread' && r.user_viewed) {
      return false;
    }
    if (filterViewed === 'read' && !r.user_viewed) {
      return false;
    }
    return true;
  });

  // Response Detail View
  if (selectedResponse) {
    return (
   <div className="modal-overlay">
        <div className="modal-content">
          {/* Header */}
          <div className={`response-header ${selectedResponse.is_urgent ? 'response-header-urgent' : 'response-header-normal'}`}>
            <div className="flex-between-start">
              <div>
                <h2 className="m-0-0-10-0">
                  {selectedResponse.is_urgent && '🚨 '}
                  Response from {selectedResponse.provider_name}
                </h2>
                <p className="m-0 opacity-90 text-14">
                  {selectedResponse.provider_type}
                  {selectedResponse.provider_specialty && ` - ${selectedResponse.provider_specialty}`}
                </p>
              </div>
              <button
                onClick={() => setSelectedResponse(null)}
                className="btn-close-white-lg"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-30">
            {/* Report Info */}
            <div className="bg-gray-100 p-15 rounded-6 mb-25">
              <div className="grid grid-1fr-1fr gap-15">
                <div>
                  <div className="data-label-sm">
                    Report
                  </div>
                  <div className="font-bold">
                    {selectedResponse.report_title}
                  </div>
                </div>
                <div>
                  <div className="data-label-sm">
                    Response Date
                  </div>
                  <div className="font-bold">
                    {formatDateOnly(selectedResponse.response_date, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }, 'en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Category Badge */}
            <div className="mb-5">
              <span className={selectedResponse.is_urgent ? "category-badge-urgent" : "category-badge-normal"}>
                {getCategoryLabel(selectedResponse.response_category)}
              </span>
            </div>

            {/* Response Text */}
            <div className="response-text-box">
              {selectedResponse.response_text}
            </div>

            {/* Actions */}
            <div className="response-actions-footer">
              <div className="text-14 text-gray-500">
                {selectedResponse.user_viewed && selectedResponse.user_viewed_at && (
                  <span>
                    ✓ Read on {new Date(selectedResponse.user_viewed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <button
                onClick={() => setSelectedResponse(null)}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Response List View
  return (
  <div className="modal-overlay">
      <div className="modal-content modal-content-xl">
        {/* Header */}
        <div className="modal-header">
            <h2 className="m-0">Provider Responses</h2>
            <button
              onClick={onClose}
              className="modal-close text-white"
            >
              &times;
            </button>
        </div>

        {/* Stats Bar */}
        {!loading && stats.total_count > 0 && (
          <div className="stats-bar">
            <div>
              <strong>{stats.total_count}</strong> Total Responses
            </div>
            {stats.unread_count > 0 && (
              <div className="text-blue font-bold">
                {stats.unread_count} Unread
              </div>
            )}
            {stats.urgent_count > 0 && (
              <div className="text-red font-bold">
                🚨 {stats.urgent_count} Urgent
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {!loading && responses.length > 0 && (
          <div className="filter-bar">
            <div>
              <label className="text-13 text-gray-500 mr-8">
                Status:
              </label>
              <select
                value={filterViewed}
                onChange={(e) => setFilterViewed(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>

            <div>
              <label className="prp-filterLabel">
                Category:
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Categories</option>
                <option value="general">General</option>
                <option value="medication_change">Medication Change</option>
                <option value="diet_recommendation">Diet</option>
                <option value="exercise_recommendation">Exercise</option>
                <option value="follow_up_required">Follow-up</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {loading && (
            <div className="empty-state">
              Loading responses...
            </div>
          )}

          {!loading && responses.length === 0 && (
            <div className="prp-emptyState">
              <div className="text-48 mb-20">📭</div>
              <h3 className="mb-3">No Responses Yet</h3>
              <p>When your healthcare providers respond to your reports, they will appear here.</p>
            </div>
          )}

          {!loading && filteredResponses.length === 0 && responses.length > 0 && (
            <div className="prp-noResults">
              No responses match your filters.
            </div>
          )}

          {/* Response Cards */}
          {!loading && filteredResponses.map((response) => (
            <div
              key={response.response_id}
              className={`prp-responseCard${response.is_urgent ? ' prp-responseCard--urgent' : ''}${!response.user_viewed ? ' prp-responseCard--unread' : ''}`}
            >
              {/* Action Buttons - Top Right */}
              <div className="prp-cardActions">
                <button onClick={() => handleViewResponse(response)} className="prp-viewBtn">
                  View Response
                </button>
                <button onClick={(e) => handleDeleteResponse(response.response_id, e)} className="prp-deleteBtn">
                  Delete
                </button>
              </div>

              {/* Status Badges - Bottom Right */}
              <div className="prp-badgeGroup">
                {response.is_urgent && (
                  <span className="prp-urgentBadge">
                    🚨 URGENT
                  </span>
                )}
                {!response.user_viewed && (
                  <span className="prp-newBadge">
                    NEW
                  </span>
                )}
              </div>

              <div className="mb-3">
                <div className="prp-providerInfo">
                  <h3 className="prp-providerName">
                    {response.provider_name}
                  </h3>
                  <span className="prp-providerType">
                    {response.provider_type}
                  </span>
                </div>
                <div className="prp-date">
                  {formatDateOnly(response.response_date, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }, 'en-US')}
                </div>
              </div>

              <div className="prp-preview">
                {response.response_text}
              </div>

              <div className="prp-meta">
                <span className="prp-categoryBadge">
                  {getCategoryLabel(response.response_category)}
                </span>
                <span className="prp-reportRef">
                  Re: {response.report_title}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ViewReportResponsesModal;
